import React, { useState, useEffect, useRef, useCallback } from 'react'
import './stepper.scss'
  
const StepperComponent = ({ steps = [], autoScroll = true }) => {  
  const [processedSteps, setProcessedSteps] = useState([])
  const [animatingIndex, setAnimatingIndex] = useState(null)
  const containerRef = useRef(null)
  const prevStepsRef = useRef([])
  
  // Process steps and fill gaps with placeholders  
  const processSteps = useCallback((rawSteps) => {
    if (!rawSteps.length) return []
    
    const result = []
    let expectedStep = rawSteps[0]?.stepNumber || 1
  
    for (const step of rawSteps) {
      // Fill gaps with placeholder steps
      let addedIntermediate = false
      while (expectedStep < step.stepNumber) {
        if (!addedIntermediate) {
          result.push({
            stepNumber: expectedStep,
            title: 'Intermediate steps',
            type: 'skipped',
            details: <><i class="far fa-info-circle" style={{marginRight: '5px'}}></i>Multiple steps were executed here.</>,
            isPlaceholder: true,
            id: `placeholder-${expectedStep}`
          })
          addedIntermediate = true
        }
        expectedStep++
      }

      let keyPrefix = step?.stepNumber

      if (!keyPrefix) {
        keyPrefix = step?.toolID || 'unknown'
      }
      result.push({
        ...step,
        isPlaceholder: false,
        id: `step-${keyPrefix}-${Date.now()}`
      })
      expectedStep = step.stepNumber + 1
    }
  
    return result
  }, [])
  
  useEffect(() => {
    const newProcessed = processSteps(steps)
    
    // Detect newly added steps for animation
    if (newProcessed.length > prevStepsRef.current.length) {
      const newIndex = newProcessed.length - 1
      setAnimatingIndex(newIndex)
      
      // Clear animation flag after animation completes  
      const timer = setTimeout(() => {
        setAnimatingIndex(null)
      }, 600)

      let scrollTimeOut = null
      
      // Auto-scroll to new step
      if (autoScroll && containerRef.current) {
        scrollTimeOut = setTimeout(() => {
          const el = containerRef.current
          el.scrollTo({
            top: el.scrollHeight,
            behavior: 'smooth'
          })
        }, 100)
      }
  
      return () => {
        clearTimeout(timer)
        clearTimeout(scrollTimeOut)
      }
    }
  
    prevStepsRef.current = newProcessed
    setProcessedSteps(newProcessed)
  }, [steps, processSteps, autoScroll])
  
  useEffect(() => {
    setProcessedSteps(processSteps(steps))
  }, [steps, processSteps])
  
  const getStepStatus = (step, index) => {  
    if (step.isPlaceholder) return 'placeholder';  
    if (step.type === 'error') return 'error';  
    if (step.type === 'completed' || step.type === 'success') return 'completed';  
    if (step.type === 'running' || step.type === 'pending') return 'running';
    if (step.type === 'done') return 'done';
    return 'default';  
  }
  
  const getStepIcon = (status) => {  
    switch (status) {  
      case 'completed':
        return (
          <i className="far fa-check"></i>
        )
      case 'error':
        return (
          <i className="far fa-exclamation-circle" style={{color: 'orange'}}></i>
        )
      case 'running':
        // return <span className="step-spinner" />
        return (
          <i className="far fa-hourglass"></i>
        )
      case 'done':
        return (
          <i className="far fa-check"></i>
        )
      case 'placeholder':
        return (
          <i className="far fa-code-commit" style={{opacity: 0.8, fontSize: '18px'}}></i>
        )
      default:
        return null
    }
  }
  
  return (  
    <div className="stepper-container" ref={containerRef} data-theme='dark'>
      <div className="stepper-wrapper">  
        {processedSteps.map((step, index) => {  
          const status = getStepStatus(step, index)
          const isLast = index === processedSteps.length - 1
          const isAnimating = index === animatingIndex
  
          return (  
            <div  
              key={step.id || index}  
              className={`stepper-step stepper-step--${status} ${isAnimating ? 'stepper-step--animating' : ''}`}  
            >
              {/* Connector Line */}
              {!isLast && (
                <div className="stepper-connector">
                  <div className="stepper-connector-line" />
                </div>
              )}
  
              {/* Step Indicator */}
              <div className="stepper-indicator">
                <div className="stepper-dot">
                  {getStepIcon(status) || (
                    <span className="stepper-number">{step.stepNumber}</span>
                  )}
                </div>
                {status === 'running' && <div className="stepper-pulse" />}
              </div>
              {/* Step Content */}
              <div className="stepper-content">
                <div className="stepper-header">
                  <h4 className="stepper-title">{step.title}</h4>
                  {status === 'completed' &&  (
                    <span className={`stepper-badge stepper-badge--${status}`}>
                      {status.toUpperCase()}
                    </span>
                  )}
                  {status === 'error' &&  (
                    <span className={`stepper-badge stepper-badge--${status}`}>
                      {status.toUpperCase()}
                      {/* <i className="far fa-exclamation-circle" style={{color: 'orange'}}></i> */}
                    </span>
                  )}
                </div>
                {step.details && (
                  <p className={`stepper-details ${status === 'running' ? 'show-before' : ''}`}>
                    {getToolIcon(step?.toolID || 'unknown')}{step.details}&nbsp;{step?.extra && (<span className='stepper-extra'>{step?.extra}</span>)}
                  </p>
                )}
                {step.timestamp && (
                  <span className="stepper-timestamp">
                    {step.stepNumber && `Step ${step.stepNumber} • `}
                    {step.timestamp}
                  </span>
                )}
              </div>
            </div>
          );  
        })}  
  
        {processedSteps.length === 0 && (
          <div className="stepper-empty">
            <div className="stepper-empty-icon">
              <i className="far fa-check-circle"></i>
            </div>
            <p>No steps yet. Waiting for agent actions...</p>
          </div>
        )}
      </div>
    </div>
  )
}
// const ids = [
//   'browser',  //
//   'search', //
//   'email',  //
//   'storage-search', //
//   'file-read',  //
//   'file-write', //
//   'terminal', //
//   'tool',   //
//   'thinking',   //
//   'interpreter',  //
//   'start',  //
//   'final'   //
// ]

const iconMap = {
  'storage-search': {
    id: 'far fa-file-search',
    color: 'rgb(198, 120, 221)'
  },
  'file-read': {
    id: 'far fa-file-alt',
    color: 'rgb(198, 120, 221)'
  },
  'file-write': {
    id: 'far fa-edit',
    color: 'rgb(198, 120, 221)'
  },
  'terminal': {
    id: 'far fa-terminal',
    color: 'orange'
  },
  'interpreter': {
    id: 'far fa-scroll',
    color: 'orange'
  },
  'email': {
    id: 'far fa-mail-bulk',
    color: 'rgb(97, 175, 239)'
  },
  'browser': {
    id: 'far fa-browser',
    color: 'rgb(97, 175, 239)'
  },
  'search': {
    id: 'far fa-compass',
    color: 'rgb(97, 175, 239)'
  },
  'thinking': {
    id: 'far fa-sparkles',
    color: 'rgb(152, 195, 121)'
  },
  'tool': {
    id: 'far fa-function',
    color: 'rgb(198, 120, 221)'
  },
  'start': {
    id: 'far fa-sparkles',
    color: 'rgb(152, 195, 121)'
  },
  'finish': {
    id: 'far fa-trophy',
    color: 'orange'
  },
  'pause': {
    id: 'far fa-pause',
    color: 'orange'
  },
  'await': {
    id: 'far fa-clock',
    color: 'rgb(97, 175, 239)'
  },
  'enrich-email': {
    id: 'far fa-clock',
    color: 'rgb(97, 175, 239)'
  }
}

const getToolIcon = (toolID) => {
  const iconSetting = iconMap[toolID] || {
    id: 'far fa-sparkles',
    color: 'rgb(152, 195, 121)'
  }
  return <i className={iconSetting.id} style={{color: iconSetting.color, marginRight: '5px'}}/>
}
  
export default StepperComponent